import { Eye } from "lucide-react";
import type { DataRecord } from "../types";
import { humanize } from "./DetailDrawer";
import { StatusBadge } from "./StatusBadge";

export interface Column {
  key: string;
  label?: string;
  kind?: "text" | "status" | "date" | "bytes" | "money";
}

export function DataTable({ rows, columns, rowKey, onInspect, actions }:
{
  rows: DataRecord[];
  columns: Column[];
  rowKey: (row: DataRecord, index: number) => string;
  onInspect?: (row: DataRecord) => void;
  actions?: (row: DataRecord) => React.ReactNode;
}) {
  if (rows.length === 0) return <div className="empty-state">No records match the current filters.</div>;
  return (
    <div className="table-scroll">
      <table className="data-table">
        <thead><tr>{columns.map((column) => <th key={column.key}>{column.label ?? humanize(column.key)}</th>)}<th><span className="sr-only">Actions</span></th></tr></thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={rowKey(row, index)}>
              {columns.map((column) => <td key={column.key}>{formatCell(row[column.key], column.kind)}</td>)}
              <td className="row-actions">
                {actions?.(row)}
                {onInspect ? <button className="icon-button" type="button" onClick={() => onInspect(row)} title="Inspect record"><Eye size={17} /></button> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatCell(value: unknown, kind: Column["kind"]) {
  if (kind === "status") return <StatusBadge value={value} />;
  if (value === null || value === undefined || value === "") return <span className="empty-value">—</span>;
  if (kind === "date") return formatDate(value);
  if (kind === "bytes" && typeof value === "number") return formatBytes(value);
  if (kind === "money" && typeof value === "number") return `$${value.toFixed(2)}`;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return <code className="compact-json">{JSON.stringify(value)}</code>;
  return <span className="cell-text" title={String(value)}>{String(value)}</span>;
}

function formatDate(value: unknown): string {
  const date = typeof value === "number" ? new Date(value * 1000) : new Date(String(value));
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KiB`;
  return `${(bytes / 1024 ** 2).toFixed(1)} MiB`;
}
